import { useForm } from "@tanstack/react-form";
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
  type ShareablePreviewFeeLine,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import {
  DEFAULT_CURRENCY,
  majorToMinor,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import {
  PAYROLL_TRANSACTION_TYPE,
  type PayrollAggregateFeeQuoteDto,
  type PayrollInitiateRequestDto,
} from "@/features/payroll/api/dto";
import { useInitiatePayrollRun } from "@/features/payroll/api/use-initiate-payroll-run";
import { usePayrollFeeQuote } from "@/features/payroll/api/use-payroll-fee-quote";
import { usePayrollTranslation } from "@/features/payroll/i18n/use-payroll-translation";
import {
  createPayrollRunSchema,
  type PayrollBeneficiaryFormValues,
  type PayrollRunFormValues,
} from "@/features/payroll/schemas";
import { CUSTOMER_SEGMENTS } from "@/features/shared/api/enums";
import {
  usePartySelectOptions,
  useWalletSelectOptions,
} from "@/features/shared/api";

type PayrollScalarValues = Omit<PayrollRunFormValues, "beneficiaries">;

const EMPTY_SCALARS: PayrollScalarValues = {
  productCode: "",
  sourceWalletId: "",
  customerSegment: "COMPANY",
  partyId: "",
  customerReference: "",
};

const emptyBeneficiary = (): PayrollBeneficiaryFormValues => ({
  destinationWalletId: "",
  amount: 0,
  label: "",
});

/** État de la preview : lot validé + devis agrégé calculé. */
interface PayrollPreview {
  values: PayrollRunFormValues;
  payloads: PayrollInitiateRequestDto[];
  quote: PayrollAggregateFeeQuoteDto;
  /** Total à débiter (principal + frais nets), unité mineure. */
  totalMinor: number;
}

const toPayload = (
  values: PayrollScalarValues,
  beneficiary: PayrollBeneficiaryFormValues
): PayrollInitiateRequestDto => ({
  type: PAYROLL_TRANSACTION_TYPE,
  productCode: values.productCode.trim(),
  sourceWalletId: values.sourceWalletId.trim(),
  destinationWalletId: beneficiary.destinationWalletId.trim(),
  destinationExternalType: "NONE",
  // Montant saisi en unité majeure → entier unité mineure (guide §4.3).
  amountMinor: majorToMinor(beneficiary.amount, DEFAULT_CURRENCY),
  currency: DEFAULT_CURRENCY,
  customerSegment: values.customerSegment,
  partyId: values.partyId?.trim() || null,
  customerReference: values.customerReference?.trim() || null,
});

/**
 * Lancement d'un lot de paie (back-office, opéré pour le compte du client) :
 * N versements `PAYROLL_CREDIT` via `POST /Transactions/initiate`
 * (wallet-service, §5.3 — un appel par bénéficiaire).
 *
 * Flux : saisie → validation zod → devis de frais AGRÉGÉ (un `POST
 * /fee-quotes` par bénéficiaire, §6.5) → `ShareablePreview` avec section
 * frais (règle projet) → initiations séquentielles idempotentes (une clé
 * fraîche par versement) → compte rendu succès/échecs.
 */
export function PayrollRunDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = usePayrollTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createPayrollRunSchema(), [i18n.language]);

  const feeQuote = usePayrollFeeQuote();
  const initiateRun = useInitiatePayrollRun();
  const { options: walletOptions, isLoading: walletsLoading } =
    useWalletSelectOptions(open);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  // Les lignes bénéficiaires vivent hors du formulaire TanStack (tableau
  // dynamique) ; elles sont validées avec le reste au submit.
  const [beneficiaries, setBeneficiaries] = useState<
    PayrollBeneficiaryFormValues[]
  >([emptyBeneficiary()]);
  /** Erreurs zod par ligne bénéficiaire (indice → message traduit). */
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [preview, setPreview] = useState<PayrollPreview | null>(null);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire et les lignes repartent des valeurs initiales.
  const form = useForm({
    defaultValues: EMPTY_SCALARS,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse({ ...value, beneficiaries });
      if (!parsed.success) {
        const errors: Record<number, string> = {};
        for (const issue of parsed.error.issues) {
          if (issue.path[0] === "beneficiaries") {
            const index = issue.path[1];
            if (typeof index === "number" && errors[index] === undefined) {
              errors[index] = issue.message;
            }
          }
        }
        setRowErrors(errors);
        return;
      }
      setRowErrors({});
      const payloads = parsed.data.beneficiaries.map((b) =>
        toPayload(parsed.data, b)
      );
      feeQuote.mutate(
        parsed.data.beneficiaries.map((b) => ({
          productCode: parsed.data.productCode.trim(),
          providerId: null,
          customerSegment: parsed.data.customerSegment,
          feePurpose: "CUSTOMER_FEE" as const,
          amountMinor: majorToMinor(b.amount, DEFAULT_CURRENCY),
          currency: DEFAULT_CURRENCY,
          // Devis avant initiate : pas de référence wallet `FPT-…`.
          // UUID unique par ligne (journal fee-engine, Postman §6.5).
          transactionReference: crypto.randomUUID(),
          beneficiaryPartyId: null,
          promoCode: null,
          partyId: parsed.data.partyId?.trim() || null,
        })),
        {
          onSuccess: (quote) =>
            setPreview({
              values: parsed.data,
              payloads,
              quote,
              totalMinor:
                payloads.reduce((sum, p) => sum + p.amountMinor, 0) +
                quote.netFeeAmountMinor,
            }),
          onError: () => notifSilenceError(t("payroll.fees.error")),
        }
      );
    },
  });

  const updateBeneficiary = (
    index: number,
    patch: Partial<PayrollBeneficiaryFormValues>
  ) =>
    setBeneficiaries((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const handleConfirm = () => {
    if (!preview) return;
    const total = preview.payloads.length;
    initiateRun.mutate(preview.payloads, {
      onSuccess: (report) => {
        if (report.failed.length === 0) {
          notifSilence(
            `${t("payroll.feedback.runSuccess")} (${report.succeeded.length}/${total})`
          );
        } else if (report.succeeded.length === 0) {
          notifSilenceError(t("payroll.feedback.runFailed"));
        } else {
          notifSilenceError(
            `${t("payroll.feedback.runPartial")} (${report.succeeded.length}/${total})`
          );
        }
        if (report.succeeded.length > 0) {
          setPreview(null);
          onOpenChange(false);
        }
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const requiredError = ({ value }: { value: unknown }) =>
    typeof value === "string" && value.trim()
      ? undefined
      : t("requiredFieldError");

  const previewFees: ShareablePreviewFeeLine[] = preview
    ? [
        {
          label: t("payroll.fees.gross"),
          amount: minorToMajor(
            preview.quote.grossFeeAmountMinor,
            preview.quote.currency
          ),
          currency: preview.quote.currency,
        },
        ...(preview.quote.discountAmountMinor > 0
          ? [
              {
                label: t("payroll.fees.discount"),
                amount: -minorToMajor(
                  preview.quote.discountAmountMinor,
                  preview.quote.currency
                ),
                currency: preview.quote.currency,
              },
            ]
          : []),
        {
          label: t("payroll.fees.net"),
          amount: minorToMajor(
            preview.quote.netFeeAmountMinor,
            preview.quote.currency
          ),
          currency: preview.quote.currency,
        },
      ]
    : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t("payroll.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <form.Field
                name="productCode"
                validators={{ onChange: requiredError }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="productCode">
                      {t("payroll.form.productCode")}
                    </Label>
                    <Input
                      id="productCode"
                      className="notranslate"
                      placeholder={t("payroll.form.productCodePlaceholder")}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="customerSegment">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label>{t("payroll.form.customerSegment")}</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) =>
                        field.handleChange(v as PayrollScalarValues["customerSegment"])
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CUSTOMER_SEGMENTS.map((v) => (
                          <SelectItem key={v} value={v} className="notranslate">
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>

              <form.Field
                name="sourceWalletId"
                validators={{ onChange: requiredError }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="sourceWalletId">
                      {t("payroll.form.sourceWalletId")}
                    </Label>
                    <EntitySearchableSelect
                      id="sourceWalletId"
                      options={walletOptions}
                      value={field.state.value}
                      onValueChange={(value) => field.handleChange(value)}
                      placeholder={t("payroll.form.sourceWalletIdPlaceholder")}
                      isLoading={walletsLoading}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {field.state.meta.errors[0]}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="partyId">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor="partyId">{t("payroll.form.partyId")}</Label>
                    <EntitySearchableSelect
                      id="partyId"
                      options={partyOptions}
                      value={field.state.value ?? ""}
                      onValueChange={(value) => field.handleChange(value)}
                      placeholder={t("payroll.form.partyIdPlaceholder")}
                      isLoading={partiesLoading}
                      clearable
                    />
                  </div>
                )}
              </form.Field>

              <form.Field name="customerReference">
                {(field) => (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="customerReference">
                      {t("payroll.form.customerReference")}
                    </Label>
                    <Input
                      id="customerReference"
                      className="notranslate"
                      placeholder={t("payroll.form.customerReferencePlaceholder")}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            <div className="space-y-2">
              <Label>{t("payroll.beneficiaries.title")}</Label>
              {beneficiaries.map((row, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-56 flex-1 space-y-1.5">
                      <EntitySearchableSelect
                        id={`payroll-beneficiary-wallet-${index}`}
                        options={walletOptions}
                        value={row.destinationWalletId}
                        onValueChange={(value) =>
                          updateBeneficiary(index, {
                            destinationWalletId: value,
                          })
                        }
                        placeholder={t(
                          "payroll.beneficiaries.walletPlaceholder"
                        )}
                        isLoading={walletsLoading}
                      />
                    </div>
                    <div className="w-36 space-y-1.5">
                      <Input
                        aria-label={t("payroll.beneficiaries.amount")}
                        type="number"
                        min={0}
                        className="notranslate"
                        placeholder={t(
                          "payroll.beneficiaries.amountPlaceholder"
                        )}
                        value={row.amount || ""}
                        onChange={(e) =>
                          updateBeneficiary(index, {
                            amount: e.target.valueAsNumber,
                          })
                        }
                      />
                    </div>
                    <div className="min-w-40 flex-1 space-y-1.5">
                      <Input
                        aria-label={t("payroll.beneficiaries.label")}
                        placeholder={t(
                          "payroll.beneficiaries.labelPlaceholder"
                        )}
                        value={row.label ?? ""}
                        onChange={(e) =>
                          updateBeneficiary(index, { label: e.target.value })
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={t("payroll.beneficiaries.remove")}
                      disabled={beneficiaries.length === 1}
                      onClick={() =>
                        setBeneficiaries((rows) =>
                          rows.filter((_, i) => i !== index)
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {rowErrors[index] && (
                    <p className="text-sm text-destructive">
                      {rowErrors[index]}
                    </p>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setBeneficiaries((rows) => [...rows, emptyBeneficiary()])
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("payroll.beneficiaries.add")}
              </Button>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={feeQuote.isPending || initiateRun.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={feeQuote.isPending || initiateRun.isPending}
              >
                {feeQuote.isPending ? <ButtonSpinner /> : null}
                {feeQuote.isPending
                  ? t("payroll.fees.pending")
                  : t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={{
            productCode: preview.values.productCode.trim(),
            sourceWalletId: preview.values.sourceWalletId.trim(),
            customerReference: preview.values.customerReference?.trim() || null,
            partyId: preview.values.partyId?.trim() || null,
            beneficiaries: preview.values.beneficiaries.map((b) => ({
              destinationWalletId: b.destinationWalletId.trim(),
              amountMinor: majorToMinor(b.amount, DEFAULT_CURRENCY),
              currency: DEFAULT_CURRENCY,
              label: b.label?.trim() || null,
            })),
          }}
          title={t("payroll.preview.title")}
          description={
            preview.quote.noActiveRule
              ? `${t("payroll.preview.description")} ${t("payroll.fees.noActiveRule")}`
              : t("payroll.preview.description")
          }
          mode="user"
          // Section frais obligatoire (cahier §9.5) : devis fee-engine agrégé
          // (un devis par bénéficiaire, sommé) calculé AVANT la preview.
          fees={previewFees}
          total={{
            label: t("payroll.preview.totalLabel"),
            amount: minorToMajor(preview.totalMinor, DEFAULT_CURRENCY),
            currency: DEFAULT_CURRENCY,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !initiateRun.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t: (key) => t(key as string),
            confirmLoading: initiateRun.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
